import get from 'lodash/get'
import includes from 'lodash/includes'
import applicationConstants from '../constants/application'
import { getNestedProp } from '../ramda'

/**
 * @const userExists
 * @description Checks possible empty states for the current user
 * @param user
 */
const userExists = user => user && Object.keys(user).length > 0

/**
 * @const getName
 * @param user
 * @returns {String} The name of the user
 */
const getFullName = user =>
  user && (user.profile.name || `${user.profile.firstName} ${user.profile.lastName}`)

/**
 * @const mustUpdatePayment
 * @param user
 * @returns {Boolean} Whether the user must provide a new payment method
 */
const mustUpdatePayment = user => user && user.stripe_needs_updated_payment

/**
 * @const isAbleToAddPayment
 * @param user
 * @returns {Boolean} Whether the user is able to actually add payment
 */
const isAbleToAddPayment = user =>
  getNestedProp('info.userType', user) === 'parent' ||
  (getNestedProp('info.userType', user) !== 'parent' && !getNestedProp('insToken', user))

/**
 * @const mustRecreateSubscription
 * @param user
 */
const mustRecreateSubscription = user => user && !user.stripe_plan_id && isAbleToAddPayment(user)

/**
 * @const isImpersonating
 * @param user
 */
const isImpersonating = user => user && user.impersonationActive

/**
 * @const filterByNestedValue
 * @param originalArray
 * @param valuesToExclude
 */
const filterByNestedValue = (originalArray, valuesToExclude) =>
  originalArray.filter(item => !includes(valuesToExclude, item.value))

/**
 * @const getUserTypeOptionsForPlan
 * @param plan
 * @returns {Array}
 */
const getUserTypeOptionsForPlan = plan => {
  const { possible_user_types, possibleUserTypesUniversity } = applicationConstants
  switch (plan) {
    case applicationConstants.stripe_plan_ids.INDIVIDUAL:
      return filterByNestedValue(possible_user_types, ['parent', 'admin'])
    case applicationConstants.stripe_plan_ids.PARENT:
      return filterByNestedValue(possible_user_types, [
        'hs_student',
        'coll_student',
        'admin',
        'professional',
      ])
    case 'admin':
      return possibleUserTypesUniversity
    case 'student':
      return possibleUserTypesUniversity
    default:
      return []
  }
}

/**
 * @const getAllowedFieldNameForUsreType
 * @param userType
 * @returns {*}
 */
const getAllowedFieldNamesForUserType = userType => {
  switch (userType) {
    case 'hs_student':
      return ['dob', 'classGrade', 'gender']
    case 'coll_student':
      return ['dob', 'classGrade', 'gender', 'collegeOrUniversity']
    case 'professional':
    case 'parent':
    case 'admin':
      return ['dob', 'gender']
    default:
      return []
  }
}

/**
 * @const getClassGradeOptions
 */
const getClassGradeOptions = userType => {
  const options = [
    {
      displayName: 'Freshman',
      value: 'freshman',
    },
    {
      displayName: 'Sophomore',
      value: 'sophomore',
    },
    {
      displayName: 'Junior',
      value: 'junior',
    },
    {
      displayName: 'Senior',
      value: 'senior',
    },
  ]

  if (userType === 'coll_student')
    options.push({ displayName: 'Post Graduate', value: 'postgraduate' })
  return options
}

/**
 * @const getGenderOptions
 */
const getGenderOptions = () => [
  {
    displayName: 'Male',
    value: 'male',
  },
  {
    displayName: 'Female',
    value: 'female',
  },
  {
    displayName: 'Other',
    value: 'other',
  },
]

const getPlanByInsTokenAndSelectedPlan = (insToken, selectedPlan) => {
  if (!insToken && !selectedPlan) return null
  if (insToken) {
    switch (insToken.substr(0, 2)) {
      case 'AD':
        return 'admin'
      case 'ST':
        return 'student'
      default:
        return null
    }
  } else {
    return selectedPlan
  }
}

const isAbleToBeOnAdminDashboard = user => user && user.insToken && user.info.userType === 'admin'
const isAbleToBeOnParentDashboard = user => user && user.info && user.info.userType === 'parent'
const isAbleToAccessDemo = user => !!get(user, 'isSystemAdministrator', false)

export default {
  userExists,
  getFullName,
  mustUpdatePayment,
  isAbleToAddPayment,
  mustRecreateSubscription,
  isImpersonating,
  getUserTypeOptionsForPlan,
  getAllowedFieldNamesForUserType,
  getClassGradeOptions,
  getGenderOptions,
  getPlanByInsTokenAndSelectedPlan,
  isAbleToBeOnAdminDashboard,
  isAbleToBeOnParentDashboard,
  isAbleToAccessDemo,
}



// WEBPACK FOOTER //
// ./src/utils/account/accountUtils.js